import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import * as mock from './mock';
import * as mockStudio from './mock_studio';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const [services, setServices] = useState(mock.services || []);
  const [projects, setProjects] = useState(mock.caseStudies || []);
  const [finishes, setFinishes] = useState(mock.finishes || []);
  const [siteConfig, setSiteConfig] = useState(mock.siteConfig || {});
  const [studioWorks, setStudioWorks] = useState(mockStudio.studioWorks || []);
  const [studioCollections, setStudioCollections] = useState(mockStudio.studioCollections || []);
  const [homepageHero, setHomepageHero] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // NOTE: POST /api/seed is admin-protected (see backend auth hardening).
        // Public visitors will get a 401 here; seeding is intentionally done
        // only from the admin panel now. We keep the call skipped rather than
        // firing a guaranteed 401 on every page load.
        const [sRes, pRes, fRes, configRes, heroRes, studioWorksRes, studioCollectionsRes] = await Promise.all([
          axios.get(`${API}/services`).catch(() => ({ data: [] })),
          axios.get(`${API}/case-studies`).catch(() => ({ data: [] })),
          axios.get(`${API}/finishes-library`).catch(() => ({ data: [] })),
          axios.get(`${API}/site-config`).catch(() => ({ data: null })),
          axios.get(`${API}/homepage`).catch(() => ({ data: null })),
          axios.get(`${API}/studio-works`).catch(() => ({ data: [] })),
          axios.get(`${API}/studio-collections`).catch(() => ({ data: [] })),
        ]);
        if (sRes.data?.length) setServices(sRes.data);
        if (pRes.data?.length) setProjects(pRes.data);
        if (fRes.data?.length) setFinishes(fRes.data);
        if (configRes.data) setSiteConfig(configRes.data);
        if (heroRes.data?.hero) setHomepageHero(heroRes.data.hero);
        
        // Studio works and collections - use API data if available, fall back to mock
        if (studioWorksRes.data?.length) setStudioWorks(studioWorksRes.data);
        if (studioCollectionsRes.data?.length) setStudioCollections(studioCollectionsRes.data);
      } catch (e) {
        console.log('Using mock data fallback');
      }
      setLoaded(true);
    };
    fetchAll();
  }, []);

  // Create the context value with all data
  // Mock data provides the base, API data overrides specific items
  // Note: We spread mock data FIRST, then override with API/state data
  const contextValue = {
    // All mock data for fallback and additional helpers
    ...mock,
    // Studio mock data for rich content (admin can override via API)
    ...mockStudio,
    // Dynamic data from API/state - these MUST come last to override mock data
    services, 
    projects, 
    finishes, 
    siteConfig,
    studioWorks,
    studioCollections,
    homepageHero,
    loaded,
  };

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
